import axios from 'axios';
import CryptoJS from 'crypto-js';
import he from 'he';

const PAGE_SIZE = 20;

const SEARCH_TYPE_MAP = {
  music: 0,
  album: 2,
  artist: 1,
  playlist: 3,
  lyric: 7,
  mv: 12
};

const SEARCH_RESULT_KEY = {
  0: 'song',
  1: 'singer',
  2: 'album',
  3: 'playlist',
  7: 'lyric',
  12: 'mv'
};

const QQ_SEARCH_URL = 'https://u.y.qq.com/cgi-bin/musicu.fcg';
const PROXY_FORWARD_URL = '/api/proxy/forward';

// QQ音乐音频获取接口（第三方代理）
const QQ_AUDIO_URL = 'https://music.haitangw.cc/music/qq_song_kw.php';

// QQ音乐歌词接口
const QQ_LYRIC_URL = 'https://c.y.qq.com/lyric/fcgi-bin/fcg_query_lyric_new.fcg';

// 音质等级映射
const QUALITY_LEVELS = {
  low: '128',
  standard: '128',
  high: '320',
  super: 'flac'
};

const buildAlbumCover = (albumMid) =>
  albumMid ? `https://y.gtimg.cn/music/photo_new/T002R800x800M000${albumMid}.jpg` : null;

const normalizeSingers = (singers = []) => {
  if (!Array.isArray(singers)) return [];
  return singers
    .map((singer) => {
      if (!singer) return '';
      if (typeof singer === 'string') return singer;
      return singer.name || singer.title || singer.nick || '';
    })
    .filter(Boolean);
};

const normalizeSong = (song) => {
  if (!song) return null;

  const albumMid = song.albummid || song.albumMid || song.album?.mid;
  const artists = normalizeSingers(song.singer);

  return {
    id: song.id || song.songid || song.songmid,
    mid: song.mid || song.songmid,
    songmid: song.songmid || song.mid,
    title: song.title || song.songname || song.name || '未知歌曲',
    artist: song.artist || artists.join(', ') || '未知艺术家',
    singer: song.singer || [],
    album: song.albumname || song.album?.title || song.album?.name || '',
    albumid: song.albumid || song.album?.id,
    albummid: albumMid,
    duration: song.interval || song.duration || 0,
    coverUrl: song.coverUrl || song.artwork || buildAlbumCover(albumMid),
    artwork: song.artwork || buildAlbumCover(albumMid),
    source: 'qq-plugin',
    raw: song
  };
};

const normalizers = {
  music: (item) => normalizeSong(item),
  album: (item) => ({
    id: item.albumID || item.albumid,
    mid: item.albumMID || item.album_mid || item.mid,
    title: item.albumName || item.albumname || item.name,
    artist: item.singerName || item.singer_name || item.author,
    artwork:
      item.albumPic ||
      buildAlbumCover(item.albumMID || item.album_mid || item.mid) ||
      null,
    description: item.desc,
    raw: item
  }),
  artist: (item) => ({
    id: item.singerID || item.id,
    mid: item.singerMID || item.singer_mid || item.mid,
    name: item.singerName || item.singer_name || item.name,
    avatar: item.avatar || item.singerPic || null,
    worksNum: item.songNum || item.song_count || 0,
    raw: item
  }),
  playlist: (item) => ({
    id: item.dissid || item.id,
    title: item.dissname || item.title,
    description: item.desc,
    playCount: item.listennum,
    artwork: item.imgurl || null,
    raw: item
  }),
  lyric: (item) => ({
    content: item.content,
    song: normalizeSong(item),
    raw: item
  })
};

const QQ_SEARCH_HEADERS = {
  accept: 'application/json, text/plain, */*',
  Referer: 'https://y.qq.com/',
  Origin: 'https://y.qq.com',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

// QQ音乐请求头（用于歌词和音频请求）
const QQ_REQUEST_HEADERS = {
  'Referer': 'https://y.qq.com/',
  'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/106.0.0.0 Safari/537.36',
  'Cookie': 'uin='
};

/**
 * 通过后端代理访问第三方接口，解决iOS跨域限制
 * @param {string} url - 目标地址
 * @param {Object} headers - 需要附带的请求头
 * @param {Object} options - 额外选项
 * @returns {Promise<any>} 后端代理返回的body内容
 */
async function requestViaBackendProxy(url, headers = {}, options = {}) {
  const proxyRequestPayload = {
    url,
    method: 'GET',
    headers,
    useDisguise: options.useDisguise ?? false
  };

  const proxyResponse = await axios.post(PROXY_FORWARD_URL, proxyRequestPayload);
  const proxyResponseData = proxyResponse?.data;

  if (!proxyResponseData || proxyResponseData.code !== 0) {
    const message = proxyResponseData?.message || '代理请求失败';
    throw new Error(`后台代理请求失败: ${message}`);
  }

  const proxiedResult = proxyResponseData.data;

  if (!proxiedResult?.success) {
    const message = proxiedResult?.message || '第三方接口响应异常';
    throw new Error(`第三方接口错误: ${message}`);
  }

  return proxiedResult.body;
}

async function searchBase(query, page = 1, type = 'music') {
  const searchType = SEARCH_TYPE_MAP[type] ?? SEARCH_TYPE_MAP.music;
  const payload = {
    req_1: {
      method: 'DoSearchForQQMusicDesktop',
      module: 'music.search.SearchCgiService',
      param: {
        num_per_page: PAGE_SIZE,
        page_num: page,
        query,
        search_type: searchType
      }
    }
  };

  const proxyRequestPayload = {
    url: QQ_SEARCH_URL,
    method: 'POST',
    headers: QQ_SEARCH_HEADERS,
    body: payload,
    contentType: 'application/json',
    useDisguise: false
  };

  const proxyResponse = await axios.post(PROXY_FORWARD_URL, proxyRequestPayload);
  const proxyResponseData = proxyResponse?.data;

  if (!proxyResponseData || proxyResponseData.code !== 0) {
    const message = proxyResponseData?.message || '代理请求失败';
    throw new Error(`QQ搜索代理异常: ${message}`);
  }

  const proxiedResult = proxyResponseData.data;

  if (!proxiedResult?.success) {
    const message = proxiedResult?.message || '第三方接口响应异常';
    throw new Error(`QQ搜索接口错误: ${message}`);
  }

  const qqResponseBody = proxiedResult.body ?? {};
  const reqData = qqResponseBody?.req_1?.data ?? {};
  const reqCode = qqResponseBody?.req_1?.code;
  const body = reqData?.body ?? {};
  const resultKey = SEARCH_RESULT_KEY[searchType];
  const result = body?.[resultKey] ?? {};
  const list = result?.list ?? [];
  const meta = reqData?.meta ?? {};
  const total = Number(result?.total ?? meta?.sum ?? body?.song?.total ?? list.length);

  if (reqCode === 2001 || (list.length === 0 && Number(meta?.estimate_sum ?? 0) > 0)) {
    console.warn('🧩 [QQ插件搜索] QQ接口返回空结果，疑似会话/鉴权失败:', {
      reqCode,
      query,
      estimateSum: meta?.estimate_sum,
      isFilter: meta?.is_filter
    });
  }

  return {
    list,
    total
  };
}

async function search(query, page = 1, type = 'music') {
  if (!query) {
    throw new Error('搜索关键词不能为空');
  }

  const { list, total } = await searchBase(query, page, type);
  const normalizer = normalizers[type] || ((item) => item);
  const data = list
    .map(normalizer)
    .filter((item) => item !== null && item !== undefined);

  return {
    isEnd: page * PAGE_SIZE >= total,
    total,
    data
  };
}

/**
 * 获取音频播放源
 * @param {Object} musicItem - 歌曲对象，必须包含 songmid 或 mid 字段
 * @param {string} quality - 音质选项：'low' | 'standard' | 'high' | 'super'
 * @returns {Promise<Object>} 返回播放源对象 { url, quality, duration, headers, lyric? }
 * @description 如果海棠网接口返回了歌词（lrc字段），会一并返回，减少后续请求
 */
async function getMediaSource(musicItem, quality = 'high') {
  if (!musicItem) {
    throw new Error('歌曲信息不能为空');
  }

  const songmid = musicItem.songmid || musicItem.mid;
  if (!songmid) {
    throw new Error('歌曲ID（songmid）不能为空');
  }

  const qualityLevel = QUALITY_LEVELS[quality] || QUALITY_LEVELS.high;
  
  console.log('🎵 [QQ插件] 开始获取音频URL:', { songmid, quality, qualityLevel });

  try {
    const audioUrl = `${QQ_AUDIO_URL}?id=${songmid}&quality=${qualityLevel}`;
    console.log('🎵 [QQ插件] 通过后端代理请求音频URL:', audioUrl);

    const responseData = await requestViaBackendProxy(audioUrl, QQ_REQUEST_HEADERS);
    console.log('🎵 [QQ插件] 代理响应数据:', responseData);
    
    // 检查响应格式
    if (!responseData) {
      throw new Error('无法获取音频URL，响应数据为空');
    }

    // 如果响应有 code 字段，说明是标准格式，需要访问 data 字段
    let audioData;
    if (responseData.code !== undefined) {
      // 标准格式：{ code: 200, data: { url: "...", lrc: "..." } }
      if (responseData.code !== 200) {
        throw new Error(`获取音频URL失败: ${responseData.msg || '未知错误'}`);
      }
      audioData = responseData.data;
    } else {
      // 直接返回数据格式：{ url: "...", lrc: "..." }
      audioData = responseData;
    }

    if (!audioData || !audioData.url) {
      throw new Error('无法获取音频URL，响应数据格式错误');
    }

    const result = {
      url: audioData.url,
      quality: quality,
      duration: musicItem.duration || 0,
      headers: {}
    };

    // 🚀 优化：如果海棠网接口返回了歌词，一并返回，减少后续请求
    if (audioData.lrc) {
      console.log('🎵 [QQ插件] 海棠网接口同时返回了歌词，一并返回');
      result.lyric = {
        lrc: audioData.lrc,
        tlyric: audioData.tlyric || '', // 翻译歌词（如果有）
        rlyric: audioData.rlyric || '' // 罗马音歌词（如果有）
      };
    }

    console.log('🎵 [QQ插件] 成功获取音频URL:', result.url);
    if (result.lyric) {
      console.log('🎵 [QQ插件] 同时获取到歌词，长度:', result.lyric.lrc?.length || 0);
    }
    return result;
  } catch (error) {
    console.error('🎵 [QQ插件] 获取音频URL失败:', error);
    throw new Error(`获取音频URL失败: ${error.message}`);
  }
}

/**
 * 获取歌词
 * @param {Object} musicItem - 歌曲对象，必须包含 songmid 或 mid 字段
 * @param {Object} options - 可选参数
 * @param {Object} options.cachedLyric - 缓存的歌词（如果 getMediaSource 已经返回了歌词）
 * @returns {Promise<Object>} 返回歌词对象 { lrc, tlyric, rlyric }
 * @description 优先使用海棠网接口获取歌词，失败时回退到QQ音乐官方接口
 */
async function getLyric(musicItem, options = {}) {
  if (!musicItem) {
    throw new Error('歌曲信息不能为空');
  }

  const songmid = musicItem.songmid || musicItem.mid;
  if (!songmid) {
    throw new Error('歌曲ID（songmid）不能为空');
  }

  // 🚀 优化：如果 getMediaSource 已经返回了歌词，直接使用
  if (options.cachedLyric) {
    console.log('🎵 [QQ插件] 使用缓存的歌词（来自 getMediaSource）');
    return options.cachedLyric;
  }

  console.log('🎵 [QQ插件] 开始获取歌词:', { songmid });

  // 🚀 优化：优先尝试从海棠网接口获取歌词
  try {
    console.log('🎵 [QQ插件] 优先尝试从海棠网接口获取歌词');
    const qualityLevel = QUALITY_LEVELS.high; // 使用默认音质
    const audioUrl = `${QQ_AUDIO_URL}?id=${songmid}&quality=${qualityLevel}`;

    const responseData = await requestViaBackendProxy(audioUrl, QQ_REQUEST_HEADERS);
    console.log('🎵 [QQ插件] 海棠网歌词代理响应数据:', responseData);
    
    // 检查响应格式
    if (!responseData) {
      throw new Error('无法获取歌词，响应数据为空');
    }

    // 如果响应有 code 字段，说明是标准格式，需要访问 data 字段
    let audioData;
    if (responseData.code !== undefined) {
      // 标准格式：{ code: 200, data: { url: "...", lrc: "..." } }
      if (responseData.code !== 200) {
        throw new Error(`获取歌词失败: ${responseData.msg || '未知错误'}`);
      }
      audioData = responseData.data;
    } else {
      // 直接返回数据格式：{ url: "...", lrc: "..." }
      audioData = responseData;
    }

    // 如果海棠网接口返回了歌词，直接使用
    if (audioData && audioData.lrc) {
      console.log('🎵 [QQ插件] 成功从海棠网接口获取歌词');
      return {
        lrc: audioData.lrc,
        tlyric: audioData.tlyric || '',
        rlyric: audioData.rlyric || ''
      };
    }
  } catch (haitangError) {
    console.warn('🎵 [QQ插件] 从海棠网接口获取歌词失败，回退到QQ音乐官方接口:', haitangError.message);
  }

  // 回退到QQ音乐官方接口（必须通过后端代理，浏览器直连会触发 CORS）
  try {
    console.log('🎵 [QQ插件] 使用QQ音乐官方接口获取歌词（后端代理）');
    const timestamp = Date.now();
    const lyricUrl = `${QQ_LYRIC_URL}?songmid=${songmid}&pcachetime=${timestamp}&g_tk=5381&loginUin=0&hostUin=0&inCharset=utf8&outCharset=utf-8&notice=0&platform=yqq&needNewCode=0`;

    console.log('🎵 [QQ插件] 歌词请求URL:', lyricUrl);

    const responseData = await requestViaBackendProxy(lyricUrl, QQ_REQUEST_HEADERS);
    const result = parseQQMusicLyricResponse(responseData);

    console.log('🎵 [QQ插件] 成功获取歌词，长度:', result.lrc.length);
    return result;
  } catch (error) {
    console.error('🎵 [QQ插件] 获取歌词失败:', error);
    throw new Error(`获取歌词失败: ${error.message}`);
  }
}

/**
 * 解析并解密 QQ 音乐歌词接口响应
 * @param {any} responseData - 代理返回的 body（可能是 JSON 对象或 JSONP 字符串）
 * @returns {{ lrc: string, tlyric: string, rlyric: string }}
 */
function parseQQMusicLyricResponse(responseData) {
  let lyricData = responseData;

  if (typeof responseData === 'string') {
    try {
      const jsonText = responseData.replace(/callback\(|MusicJsonCallback\(|jsonCallback\(|\)$/g, '');
      lyricData = JSON.parse(jsonText);
    } catch (parseError) {
      throw new Error('无法解析QQ音乐歌词响应');
    }
  }

  console.log('🎵 [QQ插件] 解析后的歌词数据:', lyricData);

  if (!lyricData || !lyricData.lyric) {
    throw new Error('无法获取歌词，响应数据格式错误');
  }

  let rawLrc = '';
  let translation = '';

  try {
    const encryptedLyric = lyricData.lyric;
    const decryptedBytes = CryptoJS.enc.Base64.parse(encryptedLyric);
    const decryptedText = CryptoJS.enc.Utf8.stringify(decryptedBytes);
    rawLrc = he.decode(decryptedText);
    console.log('🎵 [QQ插件] 歌词解密成功，长度:', rawLrc.length);
  } catch (decryptError) {
    console.warn('🎵 [QQ插件] 歌词解密失败，尝试直接使用:', decryptError);
    rawLrc = lyricData.lyric || '';
  }

  if (lyricData.trans) {
    try {
      const encryptedTrans = lyricData.trans;
      const decryptedTransBytes = CryptoJS.enc.Base64.parse(encryptedTrans);
      const decryptedTransText = CryptoJS.enc.Utf8.stringify(decryptedTransBytes);
      translation = he.decode(decryptedTransText);
      console.log('🎵 [QQ插件] 翻译歌词解密成功');
    } catch (transDecryptError) {
      console.warn('🎵 [QQ插件] 翻译歌词解密失败:', transDecryptError);
      translation = lyricData.trans || '';
    }
  }

  return {
    lrc: rawLrc,
    tlyric: translation || '',
    rlyric: ''
  };
}

const qqPluginClient = {
  platform: 'qq',
  version: '0.1.0',
  author: 'DXH',
  supportedSearchType: Object.keys(SEARCH_TYPE_MAP),
  search,
  getMediaSource,
  getLyric
};

export default qqPluginClient;

